import FormattedDate from '../../common/components/FormattedDate.js'
import ButtonLink from '../../common/components/Button/ButtonLink.js'
import {
  createStocktakeSelector,
  createStocktakeCommentGroupsSelector,
  createStocktakeWarehouseSelector,
  stocktakeStatusSelector,
  lineCountSelector,
  lineCountListSelector,
  ensureStocktakeComments,
} from '../../data/stocktakes.js'
import StocktakeTags from '../StocktakeListPage/StocktakeTags.js'
import {showAddStocktakeCommentModal} from '../StocktakeListPage/Modals/AddStocktakeCommentModal.js'
import {
  showStocktakeInternalNotesModal,
  showStocktakeReferenceIDModal,
} from '../StocktakeListPage/Modals/EditStocktakeParamModal.js'
import {stocktakeDetailReferenceIDSelector} from './stocktakeDetailFunctions.js'
import {useMemo} from 'react'
import {useSelector} from '../../store.js'
import Quantity from '../../common/components/Quantity.js'
import {STOCKTAKE_LINE_TOTAL} from '../../common/constants/Stocktakes.js'
import {Count, Plural, PluralBlock} from '../../common/components/Plural.js'
import {getWarehouseName} from '../../data/warehouses.js'
import SidebarTimeline from '../../common/components/Details/Timeline/SidebarTimeline.js'
import Icon from '../../common/icons/Icon.js'

export default function Sidebar() {
  const referenceID = useSelector(stocktakeDetailReferenceIDSelector)
  const stocktakeSelector = useMemo(
    () => createStocktakeSelector(referenceID),
    [referenceID],
  )
  const commentGroupsSelector = useMemo(
    () => createStocktakeCommentGroupsSelector(stocktakeSelector),
    [stocktakeSelector],
  )
  const stocktakeWarehouseSelector = useMemo(
    () => createStocktakeWarehouseSelector(stocktakeSelector),
    [stocktakeSelector],
  )
  const stocktake = useSelector(stocktakeSelector)
  const warehouse = useSelector(stocktakeWarehouseSelector)
  const status = useSelector((state) =>
    stocktakeStatusSelector(state, {referenceID}),
  )
  const lineCount = useSelector((state) =>
    lineCountSelector(state, {referenceID}),
  )
  const lineCountList = useSelector((state) =>
    lineCountListSelector(state, {referenceID}),
  )

  const skuCount = lineCount[STOCKTAKE_LINE_TOTAL] || 0

  return (
    <>
      <div className="wrap--batch-info-panel">
        <div className="inner-wrap__panel--actions">
          <dl className="list divider--bottom">
            <dt className="list__title--order-data">Stocktake ID</dt>
            <dd className="list__item--product-data margin-bottom-0">
              <strong className="fs-02 v-align-middle margin-right-10">
                {referenceID}
              </strong>
              <ButtonLink
                className="no-underline"
                onClick={() => showStocktakeReferenceIDModal(referenceID)}
              >
                <Icon name="pencil" className="fs-00 lh-sm v-align-middle" />
              </ButtonLink>
            </dd>
            {stocktake.tags.length !== 0 && (
              <dd className="margin-bottom-0">
                <div className="inline-block v-align-middle">
                  <StocktakeTags
                    referenceID={referenceID}
                    tags={stocktake.tags}
                  />
                </div>
              </dd>
            )}
          </dl>
          <dl className="list divider--bottom">
            <dt className="list__title--order-data">Date Created</dt>
            <dd className="list__item--product-data margin-bottom-0">
              <strong>
                <FormattedDate
                  value={stocktake.created_date}
                  format="MMM D, YYYY"
                />
                {' at '}
                <FormattedDate value={stocktake.created_date} format="h:mma" />
              </strong>
            </dd>
            <dd className="list__item--product-data">
              <em className="fs-n0 op-50">
                Updated:{' '}
                <FormattedDate
                  value={stocktake.updated_date}
                  format="MMM D, YYYY [at] h:mma"
                />
              </em>
            </dd>
          </dl>
          <dl className="list divider--bottom">
            <dt className="list__title--order-data">Contents</dt>
            <dd className="list__item--product-data">
              <strong className="fs-01 lh-sm-md">
                <PluralBlock count={skuCount}>
                  <Count /> <Plural word="SKU" />
                </PluralBlock>
              </strong>
            </dd>
            {lineCountList.length > 1 && (
              <dd className="list__item--product-data">
                <ul className="list list--square">
                  {lineCountList.map(({display, value, count}) => (
                    <li className="fs-n0 lh-sm margin-top-7" key={value}>
                      {display}: <Quantity value={count} />
                    </li>
                  ))}
                </ul>
              </dd>
            )}
          </dl>
          <dl className="list divider--bottom">
            <dt className="list__title--order-data">Status</dt>
            <dd className="list__item--product-data">
              <strong className="fs-01 lh-sm-md">{status}</strong>
            </dd>
          </dl>
          <dl className="list divider--bottom">
            <dt className="list__title--order-data">Warehouse</dt>
            <dd className="list__item--product-data">
              <strong className="fs-01 lh-sm-md">
                {getWarehouseName(warehouse)}
              </strong>
            </dd>
          </dl>
          <dl className="list divider--bottom">
            <dt className="list__title--order-data">Internal Notes</dt>
            {stocktake.internal_notes ? (
              <dd className="list__item--order-data ws-pre-wrap fs-n0">
                <div>{stocktake.internal_notes}</div>
                <ButtonLink
                  className="fs-n0 meta-batchlist-button-edit-internal-note"
                  onClick={() => showStocktakeInternalNotesModal(referenceID)}
                >
                  Edit
                </ButtonLink>
              </dd>
            ) : (
              <dd className="list__item--order-data">
                <ButtonLink
                  className="fs-n0 meta-batchlist-button-add-internal-note"
                  onClick={() => showStocktakeInternalNotesModal(referenceID)}
                >
                  Add note
                </ButtonLink>
              </dd>
            )}
          </dl>
          <SidebarTimeline
            title="Stocktake History"
            addComment={() => showAddStocktakeCommentModal(referenceID)}
            commentGroupsSelector={commentGroupsSelector}
            ensureComments={() => ensureStocktakeComments(referenceID)}
          />
        </div>
      </div>
    </>
  )
}
